import { createContext, createElement, useContext, useEffect, useState, type Dispatch, type ReactNode, type SetStateAction } from 'react';

/**
 * MVP-353. The read/write bridge between OperatorConsole's ONE `usePlaybackEngine` instance (the
 * only decoder in the app — AC-OPS-2, see that hook's own doc comment) and every OTHER console
 * screen that wants to show/control play state without opening a second one. `MiniTransportBar.tsx`
 * is the one reader today; `OperatorConsole.tsx` is the one writer.
 *
 * Two separate contexts, not one — mirroring the read/write split `usePlaybackState.ts`'s own
 * Provider/Context pair makes, for a related but distinct reason (there: one hook instance, many
 * consumers reading the SAME thing; here: one PUBLISHER and any number of possible READERS that
 * must never re-render on each other's account):
 *  - `TransportPublishContext` carries the raw `setSnapshot` setter, referentially STABLE for the
 *    life of the `<TransportBridgeProvider>` that created it (a `useState` setter never changes) —
 *    so `usePublishTransportBridge`'s caller (`OperatorConsole`) never re-renders because a reader
 *    subscribed, unsubscribed, or re-rendered off the back of a published snapshot.
 *  - `TransportSnapshotContext` carries the current snapshot itself, and DOES change on every
 *    publish — only components that call `useTransportBridgeSnapshot` (readers) re-render then.
 * Folding these into one context would mean every publish re-rendering the PUBLISHER too, for no
 * benefit — it already has the freshest copy of what it just sent.
 */
export interface TransportBridgeSnapshot {
  title: string | null;
  isPlaying: boolean;
  disabled: boolean;
  togglePlay: () => void;
  /** Milliseconds, mirroring `usePlaybackEngine`'s own `currentTimeMs`/`durationMs` — `null` while
   * nothing is loaded, same as `title`. */
  currentTimeMs: number | null;
  durationMs: number | null;
}

const TransportPublishContext = createContext<Dispatch<SetStateAction<TransportBridgeSnapshot | null>> | null>(null);

// `null` is a legitimate published value (no cockpit mounted yet, or the cockpit has no active
// song) — unlike `usePlaybackStateContext`'s `PlaybackStateContext` (whose real value is always a
// populated hook object, never falsy), this context cannot use "the value is falsy" as its own
// "no provider" signal. A sentinel distinct from every real value the context ever legitimately
// carries is what lets `useTransportBridgeSnapshot` below tell the two cases apart.
const NO_PROVIDER = Symbol('TransportSnapshotContext: no provider');
const TransportSnapshotContext = createContext<TransportBridgeSnapshot | null | typeof NO_PROVIDER>(NO_PROVIDER);

export function TransportBridgeProvider({ children }: { children: ReactNode }) {
  const [snapshot, setSnapshot] = useState<TransportBridgeSnapshot | null>(null);
  return createElement(
    TransportPublishContext.Provider,
    { value: setSnapshot },
    createElement(TransportSnapshotContext.Provider, { value: snapshot }, children)
  );
}

/** Reads the currently published snapshot — `null` while nothing has published one yet (no
 * cockpit mounted, or the cockpit has no active song). Throws outside a
 * `<TransportBridgeProvider>`, same contract as `usePlaybackStateContext` (usePlaybackState.ts): a
 * reader with no provider above it is a wiring bug, not a valid "nothing to show" state, and
 * should fail loudly at the call site rather than render nothing forever. */
export function useTransportBridgeSnapshot(): TransportBridgeSnapshot | null {
  const ctx = useContext(TransportSnapshotContext);
  if (ctx === NO_PROVIDER) throw new Error('useTransportBridgeSnapshot() must be used within a <TransportBridgeProvider>.');
  return ctx;
}

/**
 * The WRITE half — `OperatorConsole`'s one call site publishes its own engine's play state here so
 * every other console screen can read it via `useTransportBridgeSnapshot` without a second
 * `usePlaybackEngine`. Two separate effects, not one:
 *
 *  1. Republishes only when a field actually changes, even though the caller passes a fresh
 *     object literal every render (`OperatorConsole` builds `{ title, isPlaying, disabled,
 *     togglePlay }` inline) — depending on `snapshot` itself, rather than its fields, would
 *     re-publish (and re-render every reader) on every OperatorConsole render regardless of
 *     whether anything a reader cares about changed.
 *  2. Clears the snapshot only on true unmount. `publish` is a `useState` setter and therefore
 *     stable, so this cleanup never runs on a re-render — only when `OperatorConsole` itself
 *     unmounts. Folding this into effect (1) would re-run ITS cleanup (clearing the snapshot to
 *     `null`) on every dependency change there — e.g. every `isPlaying` toggle — flashing the
 *     mini-transport bar to "nothing to show" for one render before the same effect republished
 *     it. Do not merge these two effects.
 */
export function usePublishTransportBridge(snapshot: TransportBridgeSnapshot): void {
  const publish = useContext(TransportPublishContext);
  if (!publish) throw new Error('usePublishTransportBridge() must be used within a <TransportBridgeProvider>.');

  useEffect(() => {
    publish(snapshot);
  }, [publish, snapshot.title, snapshot.isPlaying, snapshot.disabled, snapshot.togglePlay, snapshot.currentTimeMs, snapshot.durationMs]);

  useEffect(() => () => publish(null), [publish]);
}
